import { Dispatch, RefObject, SetStateAction } from 'react';
import { FocusSubType, BoxSelection, TableauSelection, SalesforceSelection } from './useFocusMode';
import { FOCUS_SUPPORTED, SOURCES } from '../components/ChatInput';
import { SavedChatSelections } from '../utils/chatSelectionsStorage';

// Every value selectedSources can hold — anything else found in saved picks
// is dropped on restore. ('all' is a reset action, never a member.)
const RESTORABLE_SOURCES = new Set(SOURCES.map((s) => s.value).filter((v) => v !== 'all'));

interface Options {
  // From useDeepSearch
  deepSearch: boolean;
  setDeepSearch: Dispatch<SetStateAction<boolean>>;
  composeMode: boolean;
  setComposeMode: Dispatch<SetStateAction<boolean>>;
  webSearch: boolean;
  setWebSearch: Dispatch<SetStateAction<boolean>>;
  selectedSources: Set<string>;
  setSelectedSources: Dispatch<SetStateAction<Set<string>>>;
  setIsModeMenuOpen: Dispatch<SetStateAction<boolean>>;
  // From useFocusMode
  focusLoading: boolean;
  openFocusSourceModal: (source: FocusSubType) => Promise<void>;
  closeFocusModal: () => void;
  clearFocusSelection: (source: FocusSubType) => void;
  clearAllFocusSelections: () => void;
  restoreFocusSelections: (saved: {
    box: BoxSelection | null;
    tableau: TableauSelection | null;
    salesforce: SalesforceSelection | null;
  }) => void;
  boxSelection: BoxSelection;
  tableauSelection: TableauSelection;
  salesforceSelection: SalesforceSelection;
  // From useFileUpload
  fileUploadMode: boolean;
  deactivateFileUpload: () => void;
  activateFileUpload: (sessionId: string, email: string) => Promise<void>;
  restoreFileUpload: (sessionId: string) => Promise<void>;
  setIsFileUploadModalOpen: Dispatch<SetStateAction<boolean>>;
  // From useAtlasIntelligence
  atlasDeepInsights: boolean;
  setAtlasDeepInsights: Dispatch<SetStateAction<boolean>>;
  setIsAtlasModeMenuOpen: Dispatch<SetStateAction<boolean>>;
  // Other
  email: string | null;
  sessionIdRef: RefObject<string | null>;
}

// Orchestrates which source/mode is active across 4 independent hooks
// (useDeepSearch, useFocusMode, useFileUpload, useAtlasIntelligence). Owns
// no state of its own — every value it touches belongs to one of those
// hooks, passed in here as parameters.
export const useSourceSelection = ({
  deepSearch,
  setDeepSearch,
  composeMode,
  setComposeMode,
  webSearch,
  setWebSearch,
  selectedSources,
  setSelectedSources,
  setIsModeMenuOpen,
  focusLoading,
  openFocusSourceModal,
  closeFocusModal,
  clearFocusSelection,
  clearAllFocusSelections,
  restoreFocusSelections,
  boxSelection,
  tableauSelection,
  salesforceSelection,
  fileUploadMode,
  deactivateFileUpload,
  activateFileUpload,
  restoreFileUpload,
  setIsFileUploadModalOpen,
  atlasDeepInsights,
  setAtlasDeepInsights,
  setIsAtlasModeMenuOpen,
  email,
  sessionIdRef,
}: Options) => {
  // Opens (or reopens) the picker modal for one focus-supported source and
  // (re)fetches its items — used both when the source is first picked from
  // the + menu and when the user reopens it later via its chat-bar chip.
  // Always re-fetches (no caching), matching the rest of focus mode. Closes
  // the File Upload modal if it happened to be open — File Upload and the
  // sources are combinable now, but only one modal should be visible at once.
  const openFocusModalFor = (source: FocusSubType) => {
    setIsFileUploadModalOpen(false);
    if (!focusLoading) {
      void openFocusSourceModal(source);
    }
  };

  // Name of whatever's currently selected inside each focus-supported
  // source's picker (Box file/folder, Tableau dashboard, Salesforce table) —
  // shown on that source's chat-bar chip once picked. 'all' and 'none' carry
  // no name (both mean "everything available"), so the chip shows just the
  // source label for either.
  const focusSelectionNames: Record<string, string | null> = {
    box: boxSelection.name,
    tableau: tableauSelection.name,
    salesforce: salesforceSelection.name,
  };

  // Clears the two modes still fully exclusive with everything else — Deep
  // Search and Compose. Does NOT touch selectedSources, focus selections, or
  // File Upload, since all of those are now combinable with each other —
  // used when ADDING a source to the combination, which must not clear the
  // rest of the combination.
  const clearNonSourceModes = () => {
    setDeepSearch(false);
    setComposeMode(false);
  };

  // Clears the entire source multi-select (and every focus selection), File
  // Upload, and Deep Search/Compose — the full reset used by "All" and by
  // switching to Deep Search/Compose.
  const clearExclusiveModes = () => {
    setSelectedSources(new Set());
    clearAllFocusSelections();
    deactivateFileUpload();
    clearNonSourceModes();
  };

  // Everything picked from the + menu is scoped to the chat it was picked
  // in — used when switching to a history session so nothing carries over.
  const resetModeSelections = () => {
    clearExclusiveModes();
    setWebSearch(false);
    setAtlasDeepInsights(false);
    setIsAtlasModeMenuOpen(false);
    setIsModeMenuOpen(false);
  };

  // Add or remove one source from the multi-select combination.
  // 'all' is a reset action, not a combinable member — it clears everything.
  // Focus-supported sources (Box/Salesforce/Tableau) open their picker modal
  // immediately when added, and close it when removed.
  const handleSourceToggle = (value: string) => {
    if (value === 'all') {
      clearExclusiveModes();
      setIsModeMenuOpen(false);
      return;
    }

    const wasSelected = selectedSources.has(value);
    const next = new Set(selectedSources);
    if (wasSelected) {
      next.delete(value);
    } else {
      next.add(value);
    }
    setSelectedSources(next);

    if (wasSelected) {
      if (FOCUS_SUPPORTED.has(value)) {
        clearFocusSelection(value as FocusSubType);
      }
      return;
    }

    // Newly added
    clearNonSourceModes();
    if (FOCUS_SUPPORTED.has(value)) {
      setIsModeMenuOpen(false);
      openFocusModalFor(value as FocusSubType);
    } else if (value === 'image_generation') {
      setIsModeMenuOpen(false);
    }
    // Remaining simple toggles (atlas/box-jobit/jo-website) leave the + menu
    // open so the user can keep multi-selecting without reopening it.
  };

  // Pick the Atlas chat mode directly from the Source list's flyout — adds
  // Atlas to the source selection (if not already there) and sets
  // atlasDeepInsights in one step, then closes both popups.
  const handleSelectAtlasMode = (mode: 'normal' | 'deep_insights') => {
    if (!selectedSources.has('atlas')) {
      clearNonSourceModes();
      const next = new Set(selectedSources);
      next.add('atlas');
      setSelectedSources(next);
    }
    setAtlasDeepInsights(mode === 'deep_insights');
    setIsAtlasModeMenuOpen(false);
    setIsModeMenuOpen(false);
  };

  // Toggle Deep Search — exclusive with the source multi-select and every
  // other exclusive mode.
  const handleDeepSearchToggle = () => {
    if (deepSearch) {
      setDeepSearch(false);
    } else {
      clearExclusiveModes();
      setDeepSearch(true);
    }
    setIsModeMenuOpen(false);
  };

  // Toggle Compose — exclusive: picking it clears the whole source
  // combination and every other exclusive mode, keeping only Compose active.
  const handleComposeToggle = () => {
    if (composeMode) {
      setComposeMode(false);
    } else {
      clearExclusiveModes();
      setComposeMode(true);
    }
    setIsModeMenuOpen(false);
  };

  // Toggle web search. Fully agnostic — just flips its own flag, no longer
  // has any exclusivity with anything else (including image_generation).
  const handleWebSearchToggle = () => {
    setWebSearch((on) => !on);
  };

  // Toggle file upload mode. Combinable with the source multi-select (like
  // any other source) — only Deep Search/Compose/"All" still clear it.
  // fileUploadMode itself stays its own boolean in useFileUpload rather than
  // joining selectedSources, since it carries its own attached state
  // (uploadedFiles, its own modal) the same way Box/Salesforce/Tableau's
  // picked file/dashboard/table lives in useFocusMode rather than the set.
  const handleFileUploadToggle = async () => {
    if (fileUploadMode) {
      deactivateFileUpload();
    } else {
      clearNonSourceModes();
      closeFocusModal();
      await activateFileUpload(sessionIdRef.current ?? '', email ?? '');
    }
    setIsModeMenuOpen(false);
  };

  // ── Saved picks per chat (see chatSelectionsStorage) ──────────────────────

  // Everything picked right now, in the shape saved for a chat's latest
  // message. The models belong to useModelSelector — the caller adds them.
  const getSelectionSnapshot = (): Omit<SavedChatSelections, 'model' | 'imageModel'> => ({
    sources: Array.from(selectedSources),
    atlasMode: atlasDeepInsights ? 'deep_insights' : 'normal',
    deepSearch,
    compose: composeMode,
    webSearch,
    fileUpload: fileUploadMode,
    box: boxSelection,
    tableau: tableauSelection,
    salesforce: salesforceSelection,
  });

  // Puts a reopened chat's saved picks back, right after resetModeSelections
  // has cleared everything. Sets each value directly rather than going
  // through the toggle handlers above, which would open the Box/Tableau/
  // Salesforce pickers. Deep Search and Compose are re-checked as standalone
  // (a valid entry has at most one, and no sources alongside it), and Atlas
  // is left out when allowAtlas is false (access already known to be
  // denied). The models are restored separately (useModelSelector's
  // restoreModels) — each has its own picker, whatever the sources.
  const applySavedSelections = (saved: SavedChatSelections, allowAtlas: boolean) => {
    const known = saved.sources.filter((s) => RESTORABLE_SOURCES.has(s) && (allowAtlas || s !== 'atlas'));

    const standalone =
      saved.deepSearch ? 'deepSearch'
      : saved.compose ? 'compose'
      : null;
    const sources = new Set(standalone ? [] : known);

    setSelectedSources(sources);
    restoreFocusSelections({
      box: sources.has('box') ? saved.box : null,
      tableau: sources.has('tableau') ? saved.tableau : null,
      salesforce: sources.has('salesforce') ? saved.salesforce : null,
    });
    setAtlasDeepInsights(sources.has('atlas') && saved.atlasMode === 'deep_insights');
    setDeepSearch(standalone === 'deepSearch');
    setComposeMode(standalone === 'compose');
    setWebSearch(saved.webSearch);

    const sessionId = sessionIdRef.current;
    if (saved.fileUpload && standalone === null && sessionId) {
      void restoreFileUpload(sessionId);
    }
  };

  return {
    openFocusModalFor,
    focusSelectionNames,
    clearNonSourceModes,
    clearExclusiveModes,
    resetModeSelections,
    handleSourceToggle,
    handleSelectAtlasMode,
    handleDeepSearchToggle,
    handleComposeToggle,
    handleWebSearchToggle,
    handleFileUploadToggle,
    getSelectionSnapshot,
    applySavedSelections,
  };
};
